import { DocPage } from '../../types/doc';
import clsx from 'clsx';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface DocTableProps {
  docs?: DocPage[];
  onEdit: (doc: DocPage) => void;
  onDelete: (doc: DocPage) => void;
}

const DocTable = ({ docs, onEdit, onDelete }: DocTableProps) => {
  if (!docs) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <p className="animate-pulse text-sm text-slate-500">Loading docs...</p>
      </div>
    );
  }

  if (!docs.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-slate-500 dark:border-slate-700">
        No documentation pages yet. Start by creating one.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 shadow dark:border-slate-800">
      <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
        <thead className="bg-slate-50 dark:bg-slate-900/40">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">Title</th>
            <th className="px-4 py-3 text-left font-semibold">Category</th>
            <th className="px-4 py-3 text-left font-semibold">Status</th>
            <th className="px-4 py-3 text-left font-semibold">Updated</th>
            <th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
          {docs.map((doc) => (
            <tr key={doc._id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800">
              <td className="px-4 py-3">
                <p className="font-semibold text-slate-900 dark:text-white">{doc.title}</p>
                <p className="text-xs text-slate-500">{doc.slug}</p>
              </td>
              <td className="px-4 py-3">{doc.category}</td>
              <td className="px-4 py-3">
                <span
                  className={clsx(
                    'rounded-full px-3 py-1 text-xs font-semibold',
                    doc.published
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                  )}
                >
                  {doc.published ? 'Published' : 'Draft'}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-500">{new Date(doc.updatedAt).toLocaleDateString()}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:text-brand-500 dark:border-slate-700"
                    onClick={() => onEdit(doc)}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-rose-200 p-2 text-rose-500 hover:bg-rose-50 dark:border-rose-900/50 dark:hover:bg-rose-900/20"
                    onClick={() => onDelete(doc)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DocTable;

